import type { CompositeVerdictAudit } from "@/core/compliance/entities/CompositeVerdictAudit";
import type CompositeVerdictRepository from "@/core/compliance/repositories/CompositeVerdictRepository";
import {
  BlockingReason,
  FinalDecision,
  TrackAVerdict,
  TrackBVerdict,
  VNeuroneMode,
  type CompositeVerdict,
} from "@/core/compliance/entities/CompositeVerdict";
import { RepositoryResultFactory } from "@/core/shared/types/RepositoryResult";

interface VerdictVariant {
  finalDecision: FinalDecision;
  canCreateCustomer: boolean;
  blockingReason: BlockingReason | null;
  trackAVerdict: TrackAVerdict;
  trackBVerdict: TrackBVerdict;
  pepHit: boolean;
  sanctionsHit: boolean;
}

const CLEAN_VARIANT: VerdictVariant = {
  finalDecision: FinalDecision.CLEAN,
  canCreateCustomer: true,
  blockingReason: null,
  trackAVerdict: TrackAVerdict.CLEAN,
  trackBVerdict: TrackBVerdict.CLEAN,
  pepHit: false,
  sanctionsHit: false,
};

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function selectVariant(businessKey: string): VerdictVariant {
  const key = businessKey.toUpperCase();
  if (key.includes("PEP")) {
    return {
      finalDecision: FinalDecision.MANUAL_REVIEW,
      canCreateCustomer: false,
      blockingReason: BlockingReason.PEP_REVIEW_REQUIRED,
      trackAVerdict: TrackAVerdict.CLEAN,
      trackBVerdict: TrackBVerdict.CLEAN,
      pepHit: true,
      sanctionsHit: false,
    };
  }
  if (key.includes("SANCTION")) {
    return {
      finalDecision: FinalDecision.BLOCKED,
      canCreateCustomer: false,
      blockingReason: BlockingReason.TRACK_A_SANCTIONS_HIT,
      trackAVerdict: TrackAVerdict.BLOCKED,
      trackBVerdict: TrackBVerdict.CLEAN,
      pepHit: false,
      sanctionsHit: true,
    };
  }
  if (key.includes("PENDING")) {
    return {
      finalDecision: FinalDecision.WAIT,
      canCreateCustomer: false,
      blockingReason: BlockingReason.TRACK_A_PENDING,
      trackAVerdict: TrackAVerdict.PENDING,
      trackBVerdict: TrackBVerdict.PENDING,
      pepHit: false,
      sanctionsHit: false,
    };
  }
  if (key.includes("SOFT")) {
    return {
      finalDecision: FinalDecision.WAIT,
      canCreateCustomer: false,
      blockingReason: BlockingReason.VNEURONE_PENDING,
      trackAVerdict: TrackAVerdict.CLEAN,
      trackBVerdict: TrackBVerdict.PENDING,
      pepHit: false,
      sanctionsHit: false,
    };
  }
  if (key.includes("BLOCK")) {
    return {
      finalDecision: FinalDecision.BLOCKED,
      canCreateCustomer: false,
      blockingReason: BlockingReason.TRACK_B_BLOCKED,
      trackAVerdict: TrackAVerdict.CLEAN,
      trackBVerdict: TrackBVerdict.BLOCKED,
      pepHit: false,
      sanctionsHit: false,
    };
  }
  return CLEAN_VARIANT;
}

function buildVerdict(
  businessKey: string,
  v: VerdictVariant,
): CompositeVerdict {
  const now = new Date().toISOString();
  return {
    businessKey,
    computedAt: now,
    trackA: {
      regula: {
        availability: "PRESENT",
        screeningStatus: "CLEAR",
        documentNumber: "AB123456",
        lastScreenedAt: now,
      },
      lseg: {
        availability: "PRESENT",
        status: v.sanctionsHit ? "HIT" : "CLEAR",
        pepHit: v.pepHit,
        sanctionsHit: v.sanctionsHit,
        matchedLists: [],
        lastScreenedAt: now,
      },
      complyAdvantage: {
        availability: "PRESENT",
        status: "CLEAR",
        pepHit: false,
        sanctionsHit: false,
        matchedLists: [],
        lastScreenedAt: now,
      },
      verdict: v.trackAVerdict,
    },
    trackB: {
      vneurone: {
        availability: "PRESENT",
        outboundStatus: "VALIDATED",
        externalStatus:
          v.trackBVerdict === TrackBVerdict.BLOCKED ? "BLOCKED" : "CLEAN",
        reisReference: "REIS-MOCK-001",
        pendingOver48h: false,
        lastCalledAt: now,
        refusalRule: v.trackBVerdict === TrackBVerdict.REJECT ? "Pays_Gafi" : null,
        refusalReason:
          v.trackBVerdict === TrackBVerdict.REJECT
            ? "La nationalité ou le pays de résidence du client et/ou sa relation correspond " +
              "à l un des pays figurant sur la liste noire du GAFI"
            : null,
      },
      verdict: v.trackBVerdict,
    },
    composite: {
      scoringContext: null,
      weightTrackA: 70,
      weightTrackB: 30,
      vneuroneMode: VNeuroneMode.NON_BLOCKING,
      finalDecision: v.finalDecision,
      canCreateCustomer: v.canCreateCustomer,
      requiresScreeningHold: v.finalDecision === FinalDecision.HOLD,
      blockingReason: v.blockingReason,
    },
  };
}

export default class FakeCompositeVerdictRepositoryBackend implements CompositeVerdictRepository {
  async getVerdict(businessKey: string) {
    await new Promise((resolve) => setTimeout(resolve, 400));
    const variant = selectVariant(businessKey);
    return RepositoryResultFactory.success(buildVerdict(businessKey, variant));
  }

  /**
   * Deux évaluations : la porte a retenu le dossier sur un refus externe, puis la création est
   * restée possible. C est précisément la situation que l écran doit rendre visible.
   */
  async getAuditHistory(businessKey: string) {
    await new Promise((resolve) => setTimeout(resolve, 200));
    const variant = selectVariant(businessKey);
    const refuse = variant.trackBVerdict === TrackBVerdict.REJECT
      || variant.trackBVerdict === TrackBVerdict.BLOCKED;
    const entries: CompositeVerdictAudit[] = [
      {
        id: 2,
        businessKey,
        evaluatedAt: new Date(Date.now() - 60_000).toISOString(),
        actorId: null,
        triggerSource: "CREATE_CUSTOMER_GATE",
        finalDecision: refuse ? "HOLD" : "CLEAN",
        canCreateCustomer: true,
        blockingReason: refuse ? "TRACK_B_REJECT" : null,
        trackAVerdict: "CLEAN",
        trackBVerdict: refuse ? "REJECT" : "CLEAN",
        vneuroneOutboundStatus: refuse ? "REJECT" : "CLEAN",
        vneuroneExternalStatus: null,
        vneuroneMode: "NON_BLOCKING",
        weightTrackA: 50,
        weightTrackB: 50,
        rationale: refuse ? "outcome=HOLD trackA=CLEAN trackB=REJECT" : "outcome=CLEAN",
      },
    ];
    return RepositoryResultFactory.success(entries);
  }
}
